const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

let _accessToken = null;
const _tokenSubscribers = new Set(); // callbacks to notify on token changes

export function setAccessToken(token) {
  _accessToken = token;
  _tokenSubscribers.forEach((onTokenChange) => {
    try {
      onTokenChange(token);
    } catch (e) {
      console.error("Error in access token subscriber:", e);
    }
  });
}

export function getAccessToken() {
  // Used by Auth Context for global auth state
  return _accessToken;
}

export function subscribeAccessToken(onTokenChange) {
  // Auth Context uses this to update state on token changes
  _tokenSubscribers.add(onTokenChange);
  return () => _tokenSubscribers.delete(onTokenChange);
}

/**
 * Installation UUID of the rc_service_app, exposed by its JavaScript bridge
 * (window.RCAndroid). Returns null in a normal browser.
 */
export function getInstallationId() {
  try {
    const id = window.RCAndroid?.getInstallationId?.();
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/** Headers that identify this app installation to the auth endpoints. */
export function installationHeaders() {
  const id = getInstallationId();
  return id ? { "X-Installation-Id": id } : {};
}

export function clearTokens() {
  setAccessToken(null); // also notifies subscribers
}

function buildLoginRedirectUrl() {
  const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `/login?next=${encodeURIComponent(nextPath)}`;
}

let _redirectingToLogin = false;

function redirectToLogin() {
  // Only redirect once, and never from the login page itself. Otherwise a
  // second failing request builds "/login?next=/login?next=..." and, because
  // PublicRoute rejects a "next" that starts with /login, the user lands on
  // the homepage instead of the page they were on.
  if (_redirectingToLogin) return;
  if (window.location.pathname.startsWith("/login")) return;
  _redirectingToLogin = true;
  window.location.href = buildLoginRedirectUrl();
}

async function requestNewAccessToken() {
  const res = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
    method: "POST",
    credentials: "include", // send HttpOnly refresh cookie
    headers: { "Content-Type": "application/json", ...installationHeaders() },
  });

  if (!res.ok) {
    const err = new Error("Refresh token invalid or expired");
    // 401/403 = definitely no valid session; callers don't need to retry those.
    err.status = res.status;
    throw err;
  }

  const data = await res.json();
  return {
    accessToken: data.accessToken,
    user: {
      userId: data.userId,
      username: data.username,
      email: data.email,
      role: data.role,
      customerPkId: data.customerPkId,
      customerName: data.customerName,
      baseLocationId: data.baseLocationId,
    },
  };
}

// Every refresh rotates the refresh-token cookie, and the server only accepts
// the newest one. If two refreshes run at once (several API calls hitting 401
// together after the 5-minute access token expires, or two open tabs), the
// slower one sends the already-rotated cookie, is rejected, and used to log
// the user out. So refreshes are made single-flight within this tab, and
// serialised across tabs with the Web Locks API where it's available, so a
// waiting tab sends the fresh cookie the previous refresh just set.
let _refreshPromise = null;

function runRefreshExclusively() {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("rc-auth-refresh", requestNewAccessToken);
  }
  return requestNewAccessToken();
}

export function refreshAccessToken() {
  if (!_refreshPromise) {
    _refreshPromise = runRefreshExclusively()
      .then((result) => {
        setAccessToken(result.accessToken);
        return result;
      })
      .catch((err) => {
        clearTokens();
        throw err;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

export async function apiFetch(endpoint, options = {}) {
  const method = options.method || "GET";

  let fetchOptions = {
    method,
    credentials: "include", // ensure cookies are sent to backend if needed
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...(method !== "GET" && options.body ? { body: options.body } : {}),
  };

  // attach access token from memory if available
  const tokenUsed = _accessToken;
  if (tokenUsed) {
    fetchOptions.headers.Authorization = `Bearer ${tokenUsed}`;
  }

  let res = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

  if (res.status === 401) {
    try {
      let accessToken;
      if (_accessToken && _accessToken !== tokenUsed) {
        // Another request already refreshed while this one was in flight -
        // just retry with the new token instead of refreshing again.
        accessToken = _accessToken;
      } else {
        console.log("refreshing access token...");
        accessToken = (await refreshAccessToken()).accessToken;
      }
      fetchOptions.headers.Authorization = `Bearer ${accessToken}`;
      res = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    } catch (err) {
      // refresh failed: the session really has ended, so go to login
      redirectToLogin();
      return;
    }
  }

  return res;
}
